import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '@/utils/formatters';

function DonutChart({ data }) {
  if (!data.length) return <p className="text-xs text-gray-400 text-center py-4">No data</p>;
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <div className="flex items-center gap-4">
      <div style={{ width: 120, height: 120, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={35}
              outerRadius={55}
              paddingAngle={2}
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => formatCurrency(v)} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-col gap-1 flex-1 overflow-hidden">
        {data.map(({ name, value, color }) => (
          <div key={name} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }} />
            <span className="text-xs text-gray-600 truncate">
              {name} — {Math.round((value / total) * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DailyBarChart({ expenses }) {
  const dayMap = {};
  for (const e of expenses) {
    const day = e.date.slice(8, 10);
    dayMap[day] = (dayMap[day] ?? 0) + e.amount;
  }
  const data = Object.entries(dayMap)
    .map(([day, amount]) => ({ day: parseInt(day), amount }))
    .sort((a, b) => a.day - b.day);

  if (!data.length) return null;

  return (
    <ResponsiveContainer width="100%" height={70}>
      <BarChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <XAxis dataKey="day" tick={{ fontSize: 8 }} tickLine={false} axisLine={false} />
        <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={d => `Day ${d}`} />
        <Bar dataKey="amount" fill="#1565C0" radius={[2, 2, 0, 0]} maxBarSize={12} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function ChartsPanel({ categoryTotals, expenses, categories, paymentModes }) {
  // Build lookup maps from live context (not hardcoded constants)
  const catMap  = Object.fromEntries((categories  ?? []).map(c => [c.id, c]));
  const modeMap = Object.fromEntries((paymentModes ?? []).map(m => [m.id, m]));

  const MODE_COLORS = [
    '#1565C0','#2E7D32','#E65100','#6A1B9A','#00695C',
    '#B71C1C','#0288D1','#EF6C00','#EC407A','#78909C',
  ];

  const categoryData = Object.entries(categoryTotals)
    .map(([id, value]) => ({
      name:  catMap[id]?.label ?? id,
      value,
      color: catMap[id]?.color ?? '#90A4AE',
    }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);

  // Compute payment mode totals from expenses
  const modeTotalsMap = {};
  for (const e of expenses) {
    modeTotalsMap[e.paymentMode] = (modeTotalsMap[e.paymentMode] ?? 0) + e.amount;
  }
  const paymentData = Object.entries(modeTotalsMap)
    .map(([id, value], i) => ({
      name:  modeMap[id]?.label ?? id,
      value,
      color: modeMap[id]?.color ?? MODE_COLORS[i % MODE_COLORS.length],
    }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);

  return (
    <div className="mx-3 mb-3 card space-y-4">
      <div>
        <h3 className="text-xs font-bold text-gray-600 mb-3">📊 Category Breakdown</h3>
        <DonutChart data={categoryData} />
      </div>

      {paymentData.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-600 mb-3">💳 Payment Mode Breakdown</h3>
          <DonutChart data={paymentData} />
        </div>
      )}

      <div>
        <h3 className="text-xs font-bold text-gray-600 mb-2">📅 Daily Spend</h3>
        <DailyBarChart expenses={expenses} />
      </div>
    </div>
  );
}

